import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createWorkflowDraft } from '@/lib/api/generated/api';
import type { WorkflowView } from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';

import { WorkflowRejectedError } from '../errors';

/**
 * Creates an empty draft — the builder's "+".
 *
 * Nothing about a definition is submitted, because there is not one yet. The
 * service derives the key from the name and validates only what makes the row
 * addressable at all, so the single rejection a user can provoke here is a name
 * that is blank, too long, or whose key collides with a sibling's.
 */
export function useCreateWorkflowDraft(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      description,
    }: {
      name: string;
      description?: string | null;
    }): Promise<WorkflowView> => {
      const response = await createWorkflowDraft(workspaceId, {
        name,
        description: description ?? null,
      });

      if (response.status === 201) return response.data;
      if (response.status === 422 || response.status === 409) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      if (response.status === 404) {
        throw new Error('This workspace no longer exists.');
      }
      throw new Error('The workflow could not be created.');
    },

    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
